import React, { useEffect, useRef } from "react";
import Bird from "../Bird/Bird";
import Pipe from "../Pipe/Pipe";
import Score from "../Score/Score";
import { connect } from "react-redux";
import "./Game.css";
import { mapScore } from "../../utils/mapScore";
import StartScreen from "../StartScreen/StartScreen";
import { trackEvent, EVENT_CATEGORIES, EVENT_ACTIONS } from "../../utils/analytics";
import { CHAPTERS, getChapterIndex } from "../Score/storyline";

let gameLoop;
let pipeGenerator;

const Game = ({ status, start, fly, keypress, score }) => {
  const audioContextRef = useRef(null);

  const initializeAudio = () => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {
        console.error("Web Audio API is not supported in this browser", e);
      }
    }
    return audioContextRef.current;
  };

  const playHighTechSound = () => {
    const ctx = initializeAudio();
    if (!ctx || ctx.state === 'suspended') {
      ctx?.resume().then(() => {
        if (ctx.state === 'running') playSoundInternal(ctx);
      });
      return;
    }
    if (ctx.state === 'running') {
      playSoundInternal(ctx);
    }
  };

  const playSoundInternal = (ctx) => {
    try {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.1);

      gainNode.gain.setValueAtTime(0.06, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.2);
    } catch (e) {
      console.error("Error playing sound:", e);
    }
  };

  if (status === "game-over") {
    clearInterval(gameLoop);
    clearInterval(pipeGenerator);
  }

  const handleAction = () => {
    keypress();
    playHighTechSound();
    trackEvent(EVENT_CATEGORIES.GAME, EVENT_ACTIONS.CLICK, 'game_action', score);
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.keyCode === 32) {
        e.preventDefault(); // Prevent spacebar from scrolling
        handleAction();
      }
      if (e.keyCode === 113) {
        clearInterval(gameLoop);
        clearInterval(pipeGenerator);
        trackEvent(EVENT_CATEGORIES.GAME, EVENT_ACTIONS.CLICK, 'game_quit');
      }
    };

    const handleTouch = (e) => {
      e.preventDefault(); // Prevent default touch behaviors
      handleAction();
    };

    // Add both keyboard and touch event listeners
    document.addEventListener("keypress", handleKeyPress);
    document.addEventListener("click", handleTouch, { passive: false });
    
    // Initialize audio context on mount
    initializeAudio();
    
    // Add click listener to initialize audio on first interaction
    const handleFirstInteraction = () => {
      initializeAudio();
      trackEvent(EVENT_CATEGORIES.GAME, EVENT_ACTIONS.CLICK, 'first_interaction');
      document.removeEventListener('click', handleFirstInteraction);
    };
    document.addEventListener('click', handleFirstInteraction);

    return () => {
      window.removeEventListener("keypress", handleKeyPress);
      document.removeEventListener("click", handleTouch);
      document.removeEventListener('click', handleFirstInteraction);
    };
  }, [keypress]); // Add keypress to dependencies

  const theme = CHAPTERS[getChapterIndex(score)].theme;

  return (
    <div
      className={`Game Game--${theme} ${score >= 10 ? "Game--revealed" : ""}`}
      onTouchStart={(e) => e.preventDefault()} // Prevent unwanted touch behaviors
    >
      <Score />
      <Bird />
      <Pipe />
      <StartScreen />
    </div>
  );
};

const fly = () => {
  return (dispatch) => {
    dispatch({ type: "FLY" });
  };
};

const keypress = () => {
  return (dispatch, getState) => {
    const { status } = getState().Game;
    if (status !== "playing") {
      start()(dispatch, getState);
    }
    dispatch({ type: "FLY" });
  };
};

const start = () => {
  return (dispatch, getState) => {
    const { status } = getState().Game;
    if (status === "playing") {
      return
    }
    gameLoop && clearInterval(gameLoop);
    pipeGenerator && clearInterval(pipeGenerator);

    gameLoop = setInterval(() => {
      dispatch({ type: "FALL" });
      dispatch({ type: "RUNNING" });

      check(dispatch, getState);
    }, 150);

    pipeGenerator = setInterval(() => {
      dispatch({ type: "GENERATE" });
    }, 2000);
    dispatch({ type: "START" });
  };
};

const check = (dispatch, getState) => {
  const state = getState();
  const birdY = state.Bird.y;
  const pipes = state.Pipe.pipes;
  const x = state.Pipe.x;

    const challenge = pipes
    .map(({ topHeight }, i) => {
      return {
        x1: x + i * 200,
        y1: topHeight,
        x2: x + i * 200,
        y2: topHeight + 100,
      };
    })
    .filter(({ x1 }) => {
      if (x1 > 0 && x1 < 288) {
        return true;
      }
    });
  const windowHeight = window.innerHeight
  if (birdY > windowHeight) {
    dispatch({ type: "GAME_OVER" });
  }

//  console.log("<<< bird y", birdY, challenge)
  if (challenge.length) {
    const { x1, y1, x2, y2 } = challenge[0];
    const safetyBuffer = 8
    if (
      (x1 < 120 && 120 < x1 + 52 && birdY < (y1 - safetyBuffer) ) ||
      (x2 < 120 && 120 < x2 + 52 && birdY > (y2 - safetyBuffer))
    ) {
      dispatch({ type: "GAME_OVER" });
    }
  }
};

const mapStateToProps = ({ Game, Pipe }) => {
  return {
    status: Game.status,
    score: mapScore(Pipe),
  };
};
const mapDispatchToProps = { start, fly, keypress };

export default connect(mapStateToProps, mapDispatchToProps)(Game);
